import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/i18n";
import { legalContent, LEGAL_INFO } from "@/legal/content";

const NAV = [
  { key: "privacy", to: "/privacy" },
  { key: "cookie", to: "/cookie-policy" },
  { key: "terms", to: "/termini" },
];

const Legal = ({ doc }) => {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const content = legalContent[lang][doc];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div data-testid={`legal-page-${doc}`} className="pt-28 pb-24 px-6 sm:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3">
          <Link data-testid="legal-back-link" to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C86D51] font-semibold hover:text-[#0D253A] transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t.legal.back}
          </Link>
          <nav className="mt-8 flex flex-col gap-1 lg:sticky lg:top-28">
            {NAV.map((n) => (
              <Link
                key={n.key}
                data-testid={`legal-nav-${n.key}`}
                to={n.to}
                className={`px-4 py-2.5 rounded-full text-sm transition-colors ${
                  n.key === doc ? "bg-[#0D253A] text-[#F8F5F0]" : "text-[#5C6773] hover:bg-[#0D253A]/5"
                }`}
              >
                {t.legal[n.key]}
              </Link>
            ))}
          </nav>
        </aside>

        <motion.article
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-8 lg:col-start-5"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86D51] font-semibold">{content.eyebrow}</p>
          <h1 data-testid="legal-title" className="font-display text-4xl sm:text-5xl text-[#0D253A] mt-3">{content.title}</h1>
          <p className="text-xs text-[#5C6773] mt-4">{t.legal.updated}: {LEGAL_INFO.updated}</p>
          <p className="mt-8 text-base text-[#0D253A]/80 leading-relaxed">{content.intro}</p>

          <div className="mt-12 flex flex-col gap-10">
            {content.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-lg md:text-xl text-[#0D253A]">{s.h}</h2>
                <div className="mt-3 flex flex-col gap-3">
                  {s.p.map((para, i) => (
                    <p key={i} className="text-sm sm:text-base text-[#5C6773] leading-relaxed">{para}</p>
                  ))}
                  {s.list && (
                    <ul className="list-disc pl-5 flex flex-col gap-2">
                      {s.list.map((li, i) => (
                        <li key={i} className="text-sm sm:text-base text-[#5C6773] leading-relaxed">{li}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-[#0D253A]/10 text-sm text-[#5C6773]">
            {t.legal.contact}{" "}
            <a data-testid="legal-contact-email" href={`mailto:${LEGAL_INFO.email}`} className="text-[#C86D51] hover:underline">{LEGAL_INFO.email}</a>
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default Legal;
